import { ReactNode, useCallback, useMemo, useState } from 'react';
import { BigNumber } from 'bignumber.js';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { Resp } from '@paradigm/api/src';
import { exceptionLogger } from '@paradigm/logging/src/logging';
import {
  convertToBigNumber,
  isValidDecimal,
  unformatDecimal,
} from '@paradigm/utils/src/formatDecimal';
import { hedgeFillValue } from '#/unified-rfqs/utils/bbo-functions';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import { Side } from '#/unified-rfqs/entities/shared';
import { Order } from '#/unified-rfqs/entities/drfq/orders';
import {
  CreateOrderLeg,
  PricingResp,
} from '#/unified-rfqs/repositories/drfq-types';
import { DrfqLeg } from '#/unified-rfqs/entities/drfq/domain';
import { usePricingAlgoVersion } from '#/unified-rfqs/ui/hooks/shared';
import { useDrfqMakerOrders } from '#/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import { useQuoteBuilderView } from '#/unified-rfqs/ui/drfq/quote-builder/view';
import {
  useQuoteBuilderModalMethods,
  useQuoteBuilderModalState,
} from '#/unified-rfqs/ui/drfq/quote-builder/modal-context';
import { LegsPricesView } from '#/unified-rfqs/ui/drfq/quote-builder/views/legsPrices';
import { useDrfqContext } from '#/unified-rfqs/ui/rfq-context';
import { Maybe } from '@paradigm/utils/types';
import { isCustomStrategy } from '#/unified-rfqs/ui/rfqs/rfqs-functions';
import {
  useHedgeNettingFeatureFlag,
  useHiddenLimitOrderFeatureFlag,
} from '#/feature-flags/context';
import { CreateOrderReq } from '../../../repositories/drfq-types';
import { useQuoteBuilderActions } from './form-context';

export interface QuoteSubmitReport {
  readonly errors?: string[];
  readonly orders?: Order[];
  readonly estimatedPricesResp?: Maybe<PricingResp>;
}

type CreateQuoteByLegsParams = {
  readonly askPrices: readonly string[] | undefined;
  readonly bidPrices: readonly string[] | undefined;
};
type ReportCallback = (failedResp: QuoteSubmitReport) => void;

interface QuoteBuilderSubmit {
  readonly isLoading: boolean;
  readonly submitQuote: (cb: ReportCallback) => void;
  readonly createQuoteByLegPrices: (
    params: CreateQuoteByLegsParams,
  ) => Promise<[Resp<Order> | null, Resp<Order> | null]>;
}

export const [useQuoteBuilderSubmit, QuoteBuilderSubmitProvider] =
  createSafeContext<QuoteBuilderSubmit>('QuoteBuilderSubmitCtx');

interface Props {
  readonly children: ReactNode;
}

export const QuoteBuilderSubmit = ({ children }: Props) => {
  const { price, quantity, account, legsPrices } = useQuoteBuilderView();
  const { setLegPricing, setIsMarkPricePrefill } =
    useQuoteBuilderModalMethods();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const { bid, ask } = useDrfqMakerOrders();
  const { resetLegPrices, fillLegPricesWithMarkPrice } =
    useQuoteBuilderActions();
  const [isLoading, setIsLoading] = useState(false);
  const drfqService = useDrfqService();
  const { rfq: drfq, rfqId } = useDrfqContext();
  const isHiddenLimitOrderEnabled = useHiddenLimitOrderFeatureFlag();
  const hedgeNettingEnabled = useHedgeNettingFeatureFlag();

  const [rawBid, rawAsk] = price.value;
  // With hedge netting on the maker quotes the net structure price;
  // the priced-order endpoint prices the option legs, so hand it the
  // premium (net minus the hedge's fill value). Leg-pricing mode
  // prices each option leg from its own per-leg input, so it needs no
  // conversion - and neither does anything with hedge netting off,
  // where the input is the premium already.
  const hedgeFill =
    legPricingEnabled || !hedgeNettingEnabled
      ? BigNumber(0)
      : hedgeFillValue(drfq?.legs ?? []);
  const bidPrice = isValidDecimal(rawBid)
    ? convertToBigNumber(unformatDecimal(rawBid)).minus(hedgeFill)
    : undefined;
  const askPrice = isValidDecimal(rawAsk)
    ? convertToBigNumber(unformatDecimal(rawAsk)).minus(hedgeFill)
    : undefined;
  const isBidGreaterThanAsk =
    askPrice != null && bidPrice != null ? bidPrice.gt(askPrice) : false;
  const pricingAlgoVersion = usePricingAlgoVersion(drfq);

  const createTakerHiddenOrdersByPrice = useCallback(
    async (cb: ReportCallback) => {
      if (account == null || drfq == null) {
        cb({ errors: ['Unavailable account or RFQ. Please try again.'] });
        return;
      }

      // For TAKER hidden orders, leg prices are not included
      const args = {
        rfq_id: drfq.id,
        account_name: account,
        type: 'HIDDEN',
        time_in_force: 'GOOD_TILL_CANCELED',
        quantity: quantity.value,
      } as const;

      const createOrder = async (
        orderPrice: string | undefined,
        side: Side,
      ) => {
        if (!orderPrice) return null;

        const orderId = side === 'BUY' ? bid?.id : ask?.id;
        const order: CreateOrderReq = { ...args, side, price: orderPrice };

        return drfqService.createOrReplaceOrder({
          orderId: orderId ?? null,
          order,
        });
      };

      try {
        const [bidResp, askResp] = await Promise.all([
          createOrder(bidPrice?.toString(), 'BUY'),
          createOrder(askPrice?.toString(), 'SELL'),
        ]);

        const report = createOrderSubmitReport([bidResp, askResp]);
        cb(report);
      } catch (error) {
        cb({ errors: ['Failed to create orders. Please try again.'] });
      }
    },
    [
      account,
      drfq,
      drfqService,
      bid?.id,
      ask?.id,
      bidPrice,
      askPrice,
      quantity.value,
    ],
  );

  const createTwoWayOrder = useCallback(
    async (cb: ReportCallback) => {
      if (account == null || drfq == null) return;

      return drfqService
        .createPricedOrder({
          bid_id: bid?.id ?? null,
          ask_id: ask?.id ?? null,
          shouldUseFallbackAlgo: isCustomStrategy(drfq),
          productCodes: drfq.product_codes,
          bid_price: bidPrice?.toString(),
          ask_price: askPrice?.toString(),
          legs: drfq.legs,
          accountName: account,
          quantity: quantity.value,
          rfqId: drfq.id,
          algorithm_version: pricingAlgoVersion,
        })
        .then((resp) => {
          if (!resp.ok) {
            setLegPricing(true);
            setIsMarkPricePrefill(true);
            fillLegPricesWithMarkPrice();
            cb({
              errors: [
                `Unable to calculate sensible prices. Leg prices has been populated with mark prices as a baseline.`,
              ],
            });
            return;
          }
          if ('isPriceMismatch' in resp.data) {
            resetLegPrices();
            cb({ estimatedPricesResp: resp.data });
            return;
          }
          const report = createOrderSubmitReport(resp.data);
          cb(report);
        });
    },
    [
      account,
      drfq,
      drfqService,
      bid?.id,
      ask?.id,
      bidPrice,
      askPrice,
      quantity.value,
      pricingAlgoVersion,
      setLegPricing,
      setIsMarkPricePrefill,
      fillLegPricesWithMarkPrice,
      resetLegPrices,
    ],
  );

  const createQuoteByLegPrices = useCallback(
    async (
      params: CreateQuoteByLegsParams,
    ): Promise<[Resp<Order> | null, Resp<Order> | null]> => {
      const { askPrices = [], bidPrices = [] } = params;

      if (account == null || drfq == null) return [null, null];
      if (askPrices.length === 0 && bidPrices.length === 0) return [null, null];

      const askLegs = askPrices.map((legPrice, index) => ({
        price: legPrice,
        instrument_id: drfq.legs[index]!.instrument_id,
      }));
      const bidLegs = bidPrices.map((legPrice, index) => ({
        price: legPrice,
        instrument_id: drfq.legs[index]!.instrument_id,
      }));

      const drfqHedgeLegs = drfq.legs
        .filter((leg) => leg.isHedge)
        .map(toCreateOrderLeg);

      const args = {
        rfq_id: drfq.id,
        account_name: account,
        type: drfq.role === 'TAKER' ? 'HIDDEN' : 'LIMIT',
        time_in_force: 'GOOD_TILL_CANCELED',
        quantity: quantity.value,
      } as const;

      const createOrder = async (legs: CreateOrderLeg[], side: Side) => {
        if (legs.length === 0) return null;
        const reqLegs = legs.concat(drfqHedgeLegs);
        const orderId = side === 'BUY' ? bid?.id : ask?.id;
        return drfqService.createOrReplaceOrder({
          orderId: orderId ?? null,
          order: { ...args, legs: reqLegs, side },
        });
      };

      return Promise.all([
        createOrder(bidLegs, 'BUY'),
        createOrder(askLegs, 'SELL'),
      ]);
    },
    [account, ask?.id, bid?.id, drfq, drfqService, quantity.value],
  );

  const createLegPricesOrder = useCallback(
    async (cb: ReportCallback) => {
      const { bidLegs, askLegs } = getAsksAndBidsLegs(legsPrices);
      createQuoteByLegPrices({
        askPrices: askLegs?.map((leg) => leg.price),
        bidPrices: bidLegs?.map((leg) => leg.price),
      }).then(([bidResp, askResp]) => {
        const report = createOrderSubmitReport([bidResp, askResp]);
        cb(report);
      });
    },
    [legsPrices, createQuoteByLegPrices],
  );

  const submitQuote: QuoteBuilderSubmit['submitQuote'] = useCallback(
    (cb) => {
      if (drfq == null || rfqId == null) {
        cb({ errors: ['Unavailable Rfq. Please try again.'] });
        return;
      }

      if (account == null) {
        cb({ errors: ['Unavailable account. Please try again.'] });
        return;
      }

      if (isBidGreaterThanAsk) {
        cb({ errors: ['Bid price cannot be greater than Ask price.'] });
        return;
      }

      let createOrderFn: (cb: ReportCallback) => Promise<void>;

      if (drfq.role === 'TAKER') {
        createOrderFn = createTakerHiddenOrdersByPrice;
        if (isHiddenLimitOrderEnabled && bidPrice?.eq(askPrice ?? 0)) {
          cb({ errors: ['Bid price and Ask price cannot be the same.'] });
          return;
        }
      } else {
        createOrderFn = legPricingEnabled
          ? createLegPricesOrder
          : createTwoWayOrder;
      }

      createOrderFn(cb)
        .catch(exceptionLogger('Unexpected create drfq order catch.'))
        .finally(() => setIsLoading(false));
    },
    [
      account,
      createLegPricesOrder,
      createTwoWayOrder,
      drfq,
      isBidGreaterThanAsk,
      legPricingEnabled,
      rfqId,
      createTakerHiddenOrdersByPrice,
      isHiddenLimitOrderEnabled,
      bidPrice,
      askPrice,
    ],
  );

  const state: QuoteBuilderSubmit = useMemo(
    () => ({
      isLoading,
      submitQuote,
      createQuoteByLegPrices,
    }),
    [isLoading, submitQuote, createQuoteByLegPrices],
  );
  return (
    <QuoteBuilderSubmitProvider value={state}>
      {children}
    </QuoteBuilderSubmitProvider>
  );
};

function createOrderSubmitReport(ordersResp: (Resp<Order> | null)[]) {
  const orders: Order[] = [];
  const errors: string[] = [];
  ordersResp.forEach((resp) => {
    if (resp?.ok === true) {
      orders.push(resp.data);
    } else if (resp?.message != null) {
      errors.push(resp.message);
    }
  });

  return { errors, orders };
}

function getAsksAndBidsLegs(legsPrices: LegsPricesView) {
  const legs = legsPrices.legs.map((leg) => ({
    ...leg,
    ask: unformatDecimal(leg.ask ?? ''),
    bid: unformatDecimal(leg.bid ?? ''),
  }));

  const bidLegs = legs.map((leg) => ({
    price: leg.side === 'BUY' ? leg.bid : leg.ask,
    instrument_id: leg.instrumentId,
  }));

  const askLegs = legs.map((leg) => ({
    price: leg.side === 'BUY' ? leg.ask : leg.bid,
    instrument_id: leg.instrumentId,
  }));

  return {
    bidLegs: bidLegs.every((leg) => leg.price.length > 0) ? bidLegs : null,
    askLegs: askLegs.every((leg) => leg.price.length > 0) ? askLegs : null,
  };
}

function toCreateOrderLeg(leg: DrfqLeg): CreateOrderLeg {
  return {
    instrument_id: leg.instrument_id,
    price: leg.price!,
    quantity: leg.quantity,
  };
}
